import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api/client';
import { toast } from '../components/UI';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('tasks');
  const [pomodoro, setPomodoro] = useState(null);

  const startPomodoro = (task, phase = 'work', minutes) => {
    const m = minutes || (phase === 'work' ? 25 : 5);
    setPomodoro({
      taskId: task.id,
      taskTitle: task.title,
      phase,
      endsAt: Date.now() + m * 60 * 1000,
      isPaused: false,
      remainingSec: m * 60,
      workMinutes: phase === 'work' ? m : (settings?.pomodoro_work_minutes || 25),
      breakMinutes: phase === 'break' ? m : (settings?.pomodoro_break_minutes || 5),
    });
  };
  const pausePomodoro = () => setPomodoro(p => {
    if (!p || p.isPaused) return p;
    const remaining = Math.max(0, Math.floor((p.endsAt - Date.now()) / 1000));
    return { ...p, isPaused: true, remainingSec: remaining };
  });
  const resumePomodoro = () => setPomodoro(p => {
    if (!p || !p.isPaused) return p;
    return { ...p, isPaused: false, endsAt: Date.now() + p.remainingSec * 1000 };
  });
  const stopPomodoro = () => setPomodoro(null);
  const togglePomodoroPhase = () => setPomodoro(p => {
    if (!p) return p;
    const newPhase = p.phase === 'work' ? 'break' : 'work';
    const m = newPhase === 'work' ? p.workMinutes : p.breakMinutes;
    return { ...p, phase: newPhase, endsAt: Date.now() + m * 60 * 1000, isPaused: false, remainingSec: m * 60 };
  });

  const loadSettings = useCallback(async () => {
    try {
      const s = await api.getSettings();
      setSettings(s);
    } catch {}
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const t = await api.getTasks();
      setTasks(t);
    } catch (err) {
      console.error('Tasks konnten nicht geladen werden:', err);
      toast(err.message || 'Tasks konnten nicht geladen werden', 'error');
    }
  }, []);

  const refreshTasks = loadTasks;

  // Apply theme attribute to <html> when settings change
  useEffect(() => {
    const theme = settings?.theme === 'light' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, [settings?.theme]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      Promise.all([loadSettings(), loadTasks()]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadSettings, loadTasks]);

  const login = async (email, password) => {
    const res = await api.login(email, password);
    localStorage.setItem('token', res.token);
    localStorage.setItem('user', JSON.stringify(res.user));
    setUser(res.user);
    await Promise.all([loadSettings(), loadTasks()]);
  };

  const register = async (email, password, name) => {
    const res = await api.register(email, password, name);
    localStorage.setItem('token', res.token);
    localStorage.setItem('user', JSON.stringify(res.user));
    setUser(res.user);
    await Promise.all([loadSettings(), loadTasks()]);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setTasks([]);
    setSettings(null);
  };

  const updateSettings = async (data) => {
    const previous = settings;
    setSettings(prev => ({ ...prev, ...data }));
    try {
      const s = await api.saveSettings(data);
      setSettings(s);
      return s;
    } catch (err) {
      setSettings(previous);
      throw err;
    }
  };

  // Abschluss- und Warte-Status kommen aus den Settings, damit umbenannte
  // Statuswerte nicht stillschweigend ihre Bedeutung verlieren.
  const doneStatusValues = useMemo(
    () => settings?.done_status_values || ['Done', 'Abgebrochen'],
    [settings]
  );
  const waitingStatusValues = useMemo(
    () => settings?.waiting_status_values || ['Warten auf X'],
    [settings]
  );

  const isDone = useCallback(
    (taskOrStatus) => {
      const status = typeof taskOrStatus === 'string' ? taskOrStatus : taskOrStatus?.status;
      return doneStatusValues.includes(status);
    },
    [doneStatusValues]
  );

  const isWaiting = useCallback(
    (taskOrStatus) => {
      const status = typeof taskOrStatus === 'string' ? taskOrStatus : taskOrStatus?.status;
      return waitingStatusValues.includes(status);
    },
    [waitingStatusValues]
  );

  // "Inaktiv" heisst: der Task ist liegengeblieben. Alles, was ein bewusster
  // Zustand ist (erledigt, wartet auf jemanden, delegiert, terminiert in der
  // Zukunft) oder gar keine operative Aufgabe ist (Ziel-Ebenen), faellt raus.
  const isStale = useCallback((task) => {
    const threshold = settings?.stale_threshold_days;
    if (!threshold || !task) return false;

    if (task.scope && task.scope !== 'task') return false;   // Ziele werden naturgemaess selten angefasst
    if (isDone(task) || isWaiting(task)) return false;
    if (task.delegation === 'delegated') return false;       // liegt bei jemand anderem

    // Terminiert in der Zukunft = geplant, nicht vergessen.
    const due = task.next_step_due_date?.split('T')[0];
    if (due) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const [y, m, d] = due.split('-').map(Number);
      if (new Date(y, m - 1, d) > today) return false;
    }

    // last_activity_at wird nur bei inhaltlichen Aenderungen gesetzt; updated_at
    // ist der Fallback fuer Tasks von vor der Migration.
    const ref = task.last_activity_at || task.updated_at;
    if (!ref) return false;
    return Math.floor((Date.now() - new Date(ref)) / 86400000) >= threshold;
  }, [settings, isDone, isWaiting]);

  return (
    <AppContext.Provider value={{
      user, settings, tasks, loading, activeView,
      setActiveView, login, register, logout,
      refreshTasks, updateSettings, isStale, isDone, isWaiting,
      doneStatusValues, waitingStatusValues,
      pomodoro, startPomodoro, pausePomodoro, resumePomodoro, stopPomodoro, togglePomodoroPhase,
      statusValues: settings?.status_values || ['Offen','In Arbeit','Warten auf X','Review','Done','Abgebrochen'],
      categories: settings?.categories || [],
      importanceValues: settings?.importance_values || ['Hoch','Mittel','Niedrig'],
      urgencyValues: settings?.urgency_values || ['Sofort','Diese Woche','Irgendwann'],
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
